/**
 * Socket.IO store — manages connection lifecycle, room joins, and event relay.
 *
 * Architecture: PHP publishes events to Redis channels → Node.js Socket.IO server
 * subscribes and relays → this store receives and dispatches to Svelte stores.
 *
 * Rooms:
 *   user:{userId}  — personal notifications (follow, vote milestone, duel end, bot kudos)
 *   duel:{duelId}  — live duel events (vote, join, track submit)
 */

import type { Socket } from 'socket.io-client';
import { getUser, isLoggedIn } from '$lib/auth.svelte';
import { incrementUnread } from '$lib/stores/notifications.svelte';
import { addToast, type ToastVariant } from '$shared/toasts.svelte';

let socket: Socket | null = null;
let _connected = $state(false);
let _currentDuelId: number | null = null;

export function isConnected(): boolean {
  return _connected;
}

/**
 * Connect to Socket.IO server. Called once from AppLayout on auth.
 * Gracefully no-ops if REALTIME_URL is not configured.
 */
export async function connect(): Promise<void> {
  if (socket) return;
  // In dev, VITE_REALTIME_URL is empty — connect to same origin (Vite proxies /socket.io).
  // In production, set to https://beatduel.com for the host nginx WebSocket proxy.
  // If explicitly set to 'off', disable real-time entirely.
  const realtimeUrl = import.meta.env.VITE_REALTIME_URL;
  if (realtimeUrl === 'off') return;

  const { io } = await import('socket.io-client');
  // Pass userId + token in the handshake auth so the realtime server can
  // gate `join_user` on identity (otherwise `socket.data.userId` is null
  // and the join is silently rejected — meaning personal events never reach
  // the SPA).
  const user = getUser();
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('beatduel_token') : null;
  socket = io(realtimeUrl || undefined, {
    // Default transport order: poll first → upgrade to WebSocket.
    // Cloudflare can reject direct WebSocket handshakes; polling works reliably.
    transports: ['polling', 'websocket'],
    reconnectionAttempts: 5,
    auth: {
      userId: user?.id ?? null,
      token: token ?? null,
    },
  });

  let hasConnectedOnce = false;
  socket.on('connect', () => {
    _connected = true;
    const user = getUser();
    if (user) {
      socket!.emit('join_user', user.id);
    }
    if (_currentDuelId) {
      socket!.emit('join_duel', _currentDuelId);
    }
    if (_leaderboardSubscribers.size > 0) {
      socket!.emit('join_leaderboard');
    }
    // Reconnect callbacks fire only on a *re*connect — the initial connect
    // is already handled by callers' mount-time fetches.
    if (hasConnectedOnce) {
      _reconnectSubscribers.forEach((cb) => cb());
    }
    hasConnectedOnce = true;
  });

  socket.on('disconnect', () => {
    _connected = false;
  });

  // --- User notifications (personal) ---
  socket.on('user_notification', (event: UserNotificationEvent) => {
    if (!event?.type) return;
    const who = event.username || 'Someone';

    let message: string;
    let variant: ToastVariant;

    switch (event.type) {
      case 'duel_join':
        message = `${who} joined a duel`;
        variant = 'follow';
        break;
      case 'track_submit':
        message = `${who} submitted a track`;
        variant = 'follow';
        break;
      case 'new_follower':
        message = `${who} started following you`;
        variant = 'follow';
        break;
      case 'duel_end': {
        const name = event.duel_name ? `"${event.duel_name}"` : 'A duel';
        if (event.picked_winner) {
          message = `Your pick won ${name}!`;
          variant = 'points';
        } else {
          message = `${name} ended — check your rank!`;
          variant = 'join';
        }
        break;
      }
      case 'vote_milestone': {
        const track = event.track_name || 'Your track';
        message = `${track} reached ${event.milestone} votes!`;
        variant = 'points';
        break;
      }
      case 'bot_kudos':
        message = `${who} ${event.message || 'top-picked your track!'}`;
        variant = 'follow';
        break;
      case 'duel_ending_soon': {
        const duelName = event.duel_name ? `"${event.duel_name}"` : 'A duel';
        message = `${duelName} ends soon — see the standings`;
        variant = 'join';
        break;
      }
      case 'user_stats_updated': {
        // Personal points/rank push for the top-nav. No toast, no bell increment.
        const payload = {
          points: event.points ?? 0,
          rank: event.rank ?? 0,
          delta: event.delta ?? 0,
          season_number: event.season_number ?? 0,
        };
        _userStatsSubscribers.forEach((cb) => cb(payload));
        return;
      }
      default:
        return;
    }

    addToast(message, variant);
    incrementUnread();
    if (_onActivityUpdate) _onActivityUpdate();
  });

  // --- Duel events (live duel room, e.g. the public DuelDetail page) ---
  socket.on('duel_event', (event: DuelEvent) => {
    if (!event?.type) return;

    switch (event.type) {
      case 'track_submit':
        addToast(`${event.username || 'Someone'} submitted a track`, 'submit');
        break;
      case 'join':
        addToast(`${event.username || 'Someone'} joined the duel`, 'join');
        break;
      case 'vote':
        // Vote count updates are handled by subscribeDuelRoom's callback
        if (_duelVoteCallback) _duelVoteCallback(event);
        break;
      case 'entry_claimed':
        // Curation-duel claim events carry {duel_id, entry_count} only
        // (anonymity — docs/curation-duels.md § Anonymity). Relay to the duel
        // room subscriber so DuelDetail can refresh its lineup.
        if (_duelVoteCallback) _duelVoteCallback(event);
        break;
    }
    if (_onActivityUpdate) _onActivityUpdate();
  });

  // --- Leaderboard updates ---
  socket.on('leaderboard_update', () => {
    _leaderboardSubscribers.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        console.warn('leaderboard subscriber threw:', e);
      }
    });
  });
}

/**
 * Disconnect from Socket.IO server. Called on logout.
 */
export function disconnect(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  _connected = false;
  _currentDuelId = null;
}

// --- Duel room subscription (live vote tallies on the public DuelDetail page) ---
type DuelVoteCallback = (event: DuelEvent) => void;
let _duelVoteCallback: DuelVoteCallback | null = null;

/**
 * Subscribe to live vote events for a duel room — used by the public DuelDetail
 * page to keep tallies fresh without a page reload. Joins `duel:{duelId}` and
 * relays `vote` events to `onVote`; call the returned function to leave the room
 * and stop relaying.
 *
 * Self-contained: only one duel room is ever subscribed at a time, tracked via
 * the module-level `_currentDuelId`. Reconnection is handled for free — the
 * `connect` handler above re-emits `join_duel` for `_currentDuelId` on every
 * reconnect, so the subscription survives a socket drop without any action
 * from the caller.
 */
export function subscribeDuelRoom(duelId: number, onVote: (event: DuelEvent) => void): () => void {
  _currentDuelId = duelId;
  _duelVoteCallback = onVote;
  if (socket?.connected) {
    socket.emit('join_duel', duelId);
  }
  return () => {
    _duelVoteCallback = null;
    if (_currentDuelId && socket?.connected) {
      socket.emit('leave_duel', _currentDuelId);
    }
    _currentDuelId = null;
  };
}

// --- Activity feed updates ---
type ActivityCallback = () => void;
let _onActivityUpdate: ActivityCallback | null = null;

export function onActivityUpdate(cb: ActivityCallback | null): void {
  _onActivityUpdate = cb;
}

// --- Leaderboard live updates ---
// Multiple subscribers (e.g. top nav Header + DuelLeaderboard page) coexist
// via a Set. The `leaderboard` Socket.IO room is joined as soon as the first
// subscriber registers and left when the last one unregisters.
type LeaderboardCallback = () => void;
const _leaderboardSubscribers = new Set<LeaderboardCallback>();

export function onLeaderboardUpdate(cb: LeaderboardCallback): () => void {
  const wasEmpty = _leaderboardSubscribers.size === 0;
  _leaderboardSubscribers.add(cb);
  if (wasEmpty && socket?.connected) {
    socket.emit('join_leaderboard');
  }
  return () => {
    _leaderboardSubscribers.delete(cb);
    if (_leaderboardSubscribers.size === 0 && socket?.connected) {
      socket.emit('leave_leaderboard');
    }
  };
}

// --- Personal stats push (top nav points + rank) ---
export interface UserStatsEvent {
  points: number;
  rank: number;
  delta: number;
  /** Active weekly season number — points/rank are scoped to this season. */
  season_number: number;
}
type UserStatsCallback = (e: UserStatsEvent) => void;
const _userStatsSubscribers = new Set<UserStatsCallback>();

export function onUserStatsUpdate(cb: UserStatsCallback): () => void {
  _userStatsSubscribers.add(cb);
  return () => _userStatsSubscribers.delete(cb);
}

// --- Reconnect callbacks (so subscribers can refetch state they may have missed) ---
type ReconnectCallback = () => void;
const _reconnectSubscribers = new Set<ReconnectCallback>();

export function onSocketReconnect(cb: ReconnectCallback): () => void {
  _reconnectSubscribers.add(cb);
  return () => _reconnectSubscribers.delete(cb);
}

// --- Event types ---
interface UserNotificationEvent {
  type: string;
  username?: string;
  duel_name?: string;
  track_name?: string;
  milestone?: number;
  message?: string;
  picked_winner?: boolean;
  points?: number;
  rank?: number;
  delta?: number;
  season_number?: number;
}

export interface DuelEvent {
  type: string;
  username?: string;
  track_id?: number;
  vote_count?: number;
  /** Curation claim events (`entry_claimed`) — identity-free by design. */
  duel_id?: number;
  entry_count?: number;
}
